/* eslint-disable no-param-reassign */
import {Trans} from '@lingui/macro';
import produce, {Draft} from 'immer';
import React, {FC, useCallback, useMemo} from 'react';

import {HandlerOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {useBaseEditorContext} from '../../../../editors/baseEditorContext';
import {Parameter} from '../../../../editors/commons/parameterModel';
import {ClientAtom} from '../../../../editors/expressions/expressionAtomModel';
import {filterOutPreviousExpressions} from '../../../../editors/expressions/expressionInferHelpers';
import {Expression} from '../../../../editors/expressions/expressionModel';
import {useExpressionsContext} from '../../../../editors/expressions/expressionsContext';
import {RuleDefaultCardTypesEnum} from '../../../ruleActionConditionHelpers';
import {InputBoolean} from '../../inputs/inputBoolean';
import {InputNumber} from '../../inputs/inputNumber';
import {InputStringTemplateParameter} from '../../inputs/inputStringTemplateParameter';
import {AnswerParameter} from '../../parameters/answerParameter';
import {ChannelParameter, ChannelsParameter} from '../../parameters/channelParameter';
import {CircleParameter} from '../../parameters/circleParameter';
import {CustomFieldParameter} from '../../parameters/customFieldParameter';
import {EnumParameter} from '../../parameters/enumParameter';
import {ExpressionParameter, ExpressionsParameter} from '../../parameters/expressionParameter';
import {GroupParameter} from '../../parameters/groupParameter';
import {InboxesParameter, InboxParameter} from '../../parameters/inboxParameter';
import {TagParameter, TagsParameter} from '../../parameters/tagParameter';
import {TeammateParameter, TeammatesParameter} from '../../parameters/teammateParameter';
import {TimezoneParameter} from '../../parameters/timezoneParameter';

const {
  isArray,
  isChannel,
  isCustomField,
  isInbox,
  isEnum,
  isGroup,
  isNumber,
  isResponse,
  isExpression,
  isString,
  isTag,
  isTeammate,
  isTimezone,
  isBoolean,
  isCircle,
} = Parameter;

export interface AtomParameterPickerProps {
  atom: ClientAtom;
  parameter?: Parameter;
  isDisabled?: boolean;

  onUpdate: HandlerOf<ClientAtom>;
}

export const AtomParameterPicker: FC<AtomParameterPickerProps> = ({
  atom,
  parameter,
  isDisabled,
  onUpdate,
}) => {
  const {namespace} = useBaseEditorContext();
  const {expressions: allExpressions} = useExpressionsContext();

  const expressions: ReadonlyArray<Expression> = useMemo(
    () => filterOutPreviousExpressions(allExpressions, atom),
    [atom, allExpressions],
  );

  const currentValue: any = parameter ? atom.params[parameter.id] : undefined;

  const updateParameter = useCallback(
    (newParameterValue: any) =>
      onUpdate(
        produce(atom, (draft: Draft<ClientAtom>) => {
          if (!parameter) {
            return;
          }

          if (!draft.params) {
            draft.params = {};
          }

          draft.params[parameter.id] = newParameterValue;
        }),
      ),
    [onUpdate, atom, parameter],
  );

  // This can happen if the inferrence is in an invalid state. For example, if a get_custom_field was
  // inferring to string and is now number, we won't be able to list the right parameters.
  if (!parameter) {
    return null;
  }

  if (isEnum(parameter)) {
    return (
      <EnumParameter
        enumParameter={parameter}
        selectedItem={currentValue}
        isDisabled={isDisabled}
        onChange={updateParameter}
      />
    );
  }

  if (isTimezone(parameter)) {
    return (
      <TimezoneParameter selectedItem={currentValue} onChange={updateParameter} isDisabled={isDisabled} />
    );
  }

  if (isString(parameter)) {
    return (
      <InputStringTemplateParameter
        stringParameter={parameter}
        value={currentValue}
        onValueChange={updateParameter}
        isDisabled={isDisabled}
        expressions={expressions}
        atom={atom}
      />
    );
  }

  if (isTag(parameter)) {
    return (
      <TagParameter
        selectedItem={currentValue}
        expressions={expressions}
        isDisabled={isDisabled}
        onChange={updateParameter}
      />
    );
  }

  if (isArray(parameter) && isTag(parameter.items)) {
    return (
      <TagsParameter
        selectedItems={currentValue}
        expressions={expressions}
        isDisabled={isDisabled}
        onChange={updateParameter}
      />
    );
  }

  if (isChannel(parameter)) {
    return (
      <ChannelParameter
        selectedItem={currentValue}
        isDisabled={isDisabled}
        onChange={updateParameter}
        namespace={namespace}
      />
    );
  }

  if (isArray(parameter) && isChannel(parameter.items)) {
    return (
      <ChannelsParameter
        selectedItems={currentValue}
        isDisabled={isDisabled}
        onChange={updateParameter}
        namespace={namespace}
        canSelectAnyItem
      />
    );
  }

  if (isGroup(parameter)) {
    return <GroupParameter selectedItem={currentValue} onChange={updateParameter} isDisabled={isDisabled} />;
  }

  if (isTeammate(parameter)) {
    return (
      <TeammateParameter
        selectedItem={currentValue}
        expressions={expressions}
        isDisabled={isDisabled}
        onChange={updateParameter}
      />
    );
  }

  if (isArray(parameter) && isTeammate(parameter.items)) {
    return (
      <TeammatesParameter
        selectedItems={currentValue}
        expressions={expressions}
        isDisabled={isDisabled}
        onChange={updateParameter}
        selectedItemsSeparator={<Trans id="rule-picker-and">and</Trans>}
      />
    );
  }

  if (isCircle(parameter)) {
    return <CircleParameter selectedItem={currentValue} isDisabled={isDisabled} onChange={updateParameter} />;
  }

  if (isInbox(parameter)) {
    return (
      <InboxParameter
        selectedItem={currentValue}
        expressions={expressions}
        isDisabled={isDisabled}
        onChange={updateParameter}
        type={RuleDefaultCardTypesEnum.CONDITION}
        namespace={namespace}
      />
    );
  }

  if (isArray(parameter) && isInbox(parameter.items)) {
    return (
      <InboxesParameter
        selectedItems={currentValue}
        expressions={expressions}
        isDisabled={isDisabled}
        onChange={updateParameter}
        type={RuleDefaultCardTypesEnum.CONDITION}
        namespace={namespace}
        canSelectAnyItem
      />
    );
  }

  if (isResponse(parameter)) {
    return (
      <AnswerParameter
        selectedItem={currentValue}
        onChange={updateParameter}
        isDisabled={isDisabled}
        namespace={namespace}
      />
    );
  }

  if (isCustomField(parameter)) {
    return (
      <CustomFieldParameter
        selectedItem={currentValue}
        onCustomFieldChange={updateParameter}
        isDisabled={isDisabled}
        atom={atom}
      />
    );
  }

  if (isExpression(parameter)) {
    return (
      <ExpressionParameter
        selectedItem={currentValue}
        expressions={expressions}
        isDisabled={isDisabled}
        onChange={updateParameter}
        atom={atom}
      />
    );
  }

  if (isArray(parameter) && isExpression(parameter.items)) {
    return (
      <ExpressionsParameter
        selectedItems={currentValue}
        expressions={expressions}
        isDisabled={isDisabled}
        onChange={updateParameter}
        atom={atom}
        expressionFormat={parameter.items.expressionFormat}
      />
    );
  }

  if (isBoolean(parameter)) {
    return (
      <InputBoolean
        booleanParameter={parameter}
        currentValue={currentValue}
        onValueChange={updateParameter}
        isDisabled={isDisabled}
      />
    );
  }

  // Number parameter has the lowest priority as it shouldn't override any other model that uses a number input underneath.
  if (isNumber(parameter)) {
    return (
      <InputNumber
        parameter={parameter}
        value={currentValue}
        onValueChange={updateParameter}
        isDisabled={isDisabled}
        expressions={expressions}
      />
    );
  }

  // Fallback for non-implemented actions / conditions.
  return <div>{parameter.id}</div>;
};
